import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { mapProfile } from "@/lib/adapt";
import { useAuth } from "@/context/AuthContext";
import { useCelebrate } from "@/context/MatchCelebration";
import { ProfileCard } from "@/components/ProfileCard";
import { MatchSpotlight } from "@/components/MatchSpotlight";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Star, Globe, Search, Sparkles, Loader2, MailWarning, ChevronRight, X, Heart, EyeOff } from "lucide-react";

const FREE_INTEREST_LIMIT = 3;

const TABS = [
  { key: "curated", label: "Curated", icon: Star },
  { key: "browse", label: "Browse", icon: Globe },
  { key: "search", label: "Search", icon: Search },
];

const SORTS = [
  { key: "most_compatible", label: "Most Compatible" },
  { key: "recently_joined", label: "Recently Joined" },
  { key: "recently_active", label: "Recently Active" },
];

export default function Discovery() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const celebrate = useCelebrate();
  const [tab, setTab] = useState("curated");
  const [sort, setSort] = useState("most_compatible");
  const [profiles, setProfiles] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [verifyDismissed, setVerifyDismissed] = useState(() => sessionStorage.getItem("nikah_verify_nudge_dismissed") === "1");
  const showVerifyNudge = !user?.email_verified && !verifyDismissed;
  const dismissVerify = () => { sessionStorage.setItem("nikah_verify_nudge_dismissed", "1"); setVerifyDismissed(true); };
  const isPremium = !!user?.is_premium;
  const isAssistant = !!user?.is_assistant;
  const [usedInterests, setUsedInterests] = useState(Number(user?.raw?.interestsCount ?? 0));
  const remaining = Math.max(0, FREE_INTEREST_LIMIT - usedInterests);
  const outOfInterests = remaining === 0;

  const load = useCallback(async (nextPage = 1) => {
    if (nextPage === 1) setProfiles(null);
    else setLoadingMore(true);
    try {
      if (tab === "browse") {
        const { data } = await api.get("/browse", { params: { sort, page: nextPage, limit: 12 } });
        const mapped = (data.profiles || []).map(mapProfile).filter((p) => !p.hidden);
        setProfiles((prev) => (nextPage === 1 ? mapped : [...(prev || []), ...mapped]));
        setHasMore(!!data.hasMore);
        setPage(data.page || nextPage);
      } else {
        const { data } = await api.get("/discovery");
        setProfiles((data.profiles || []).map(mapProfile).filter((p) => !p.hidden));
        setHasMore(false);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setProfiles([]);
    } finally {
      setLoadingMore(false);
    }
  }, [tab, sort]);

  useEffect(() => { load(1); }, [load]);

  const decide = async (id, action) => {
    const target = (profiles || []).find((p) => p.id === id);
    setProfiles((prev) => prev.filter((p) => p.id !== id));
    try {
      const { data } = await api.post("/actions", { targetId: id, action });
      if (data.limitReached) {
        toast.error(data.message || "You've used your 3 free interests this month. Upgrade to Premium for unlimited interests.");
        setUsedInterests(FREE_INTEREST_LIMIT);
        load(1);
        return;
      }
      if (data.matched) celebrate(target);
      else if (action === "interested") toast.success("Interest sent");
      if (action === "interested" && !isPremium) setUsedInterests((u) => u + 1);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      load(1);
    }
  };

  return (
    <div>
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-700 to-emerald-900 text-amber-300 shadow-sm"><Sparkles className="h-6 w-6" /></div>
        <div>
          <h1 className="font-serif text-3xl font-bold text-emerald-950 sm:text-4xl">Discover</h1>
          <p className="text-sm text-slate-500">Curated quality — or browse &amp; search freely</p>
        </div>
      </div>

      {!isPremium && !isAssistant && (
        <div data-testid="free-interest-counter" className={`mb-5 flex items-center justify-between gap-3 rounded-2xl px-4 py-3 ring-1 ${outOfInterests ? "bg-amber-100/70 ring-amber-300/60" : "bg-mint/60 ring-emerald-900/10"}`}>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm">
            <Heart className={`h-4 w-4 ${outOfInterests ? "text-amber-600" : "text-emerald-700"}`} />
            {outOfInterests ? (
              <span className="font-semibold text-amber-900" data-testid="free-interest-count-text">You've used all {FREE_INTEREST_LIMIT} free interests this month</span>
            ) : (
              <>
                <span className="font-semibold text-emerald-900" data-testid="free-interest-count-text">{remaining} of {FREE_INTEREST_LIMIT} free interests left</span>
                <span className="text-slate-500">this month</span>
              </>
            )}
          </div>
          <button onClick={() => navigate("/premium")} data-testid="free-interest-upgrade" className={`shrink-0 rounded-full px-3.5 py-1.5 text-xs font-semibold text-white ${outOfInterests ? "bg-amber-600 hover:bg-amber-700" : "bg-emerald-800 hover:bg-emerald-900"}`}>{outOfInterests ? "Upgrade" : "Go unlimited"}</button>
        </div>
      )}

      {showVerifyNudge && (
        <div data-testid="discovery-verify-nudge" className="mb-5 flex items-center gap-3 rounded-2xl bg-amber-100/70 p-4 ring-1 ring-amber-300/50">
          <MailWarning className="h-6 w-6 shrink-0 text-amber-600" />
          <button onClick={() => navigate("/verify-email")} data-testid="discovery-verify-cta" className="min-w-0 flex-1 text-left">
            <p className="font-bold text-amber-900">Verify your email</p>
            <p className="text-sm text-amber-800/90">Confirm your email to earn your verified badge and unlock reporting.</p>
          </button>
          <button onClick={() => navigate("/verify-email")} className="hidden shrink-0 rounded-full bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700 sm:inline-flex">Verify now</button>
          <ChevronRight className="h-5 w-5 shrink-0 text-amber-600 sm:hidden" />
          <button onClick={dismissVerify} data-testid="discovery-verify-dismiss" aria-label="Dismiss" className="shrink-0 rounded-full p-1 text-amber-600/70 hover:bg-amber-200/60 hover:text-amber-800"><X className="h-4 w-4" /></button>
        </div>
      )}

      <MatchSpotlight />

      {user?.hidden && (
        <div data-testid="discovery-hidden-banner" className="mb-5 flex items-center gap-3 rounded-2xl bg-slate-900/90 p-4 ring-1 ring-slate-700/40">
          <EyeOff className="h-6 w-6 shrink-0 text-amber-300" />
          <div className="min-w-0 flex-1">
            <p className="font-bold text-white" data-testid="discovery-hidden-banner-title">Your profile is hidden</p>
            <p className="text-sm text-slate-300">While hidden, you won't appear in anyone's discovery, search, or suggested matches — so your reach is paused.</p>
          </div>
          <button onClick={() => navigate("/profile")} data-testid="discovery-hidden-banner-cta" className="shrink-0 rounded-full bg-amber-400 px-4 py-2 text-sm font-semibold text-emerald-950 hover:bg-amber-300">Make me visible</button>
        </div>
      )}


      {/* Segmented tab toggle */}
      <div className="mb-4 flex gap-2 rounded-2xl bg-white p-1.5 shadow-sm ring-1 ring-emerald-900/5 sm:inline-flex">
        {TABS.map(({ key, label, icon: Icon }) => {
          const active = tab === key;
          return (
            <button
              key={key}
              data-testid={`discover-tab-${key}`}
              onClick={() => { if (key === "search") navigate("/search"); else { setTab(key); setPage(1); } }}
              className={`flex flex-1 items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold transition-colors sm:flex-none ${active ? "bg-emerald-800 text-white shadow" : "text-emerald-900/70 hover:bg-mint"}`}
            >
              <Icon className={`h-4 w-4 ${active ? "text-amber-300" : ""}`} /> {label}
            </button>
          );
        })}
      </div>

      {/* Sub-filter pills (Browse only) */}
      {tab === "browse" && (
        <div className="mb-6 flex flex-wrap gap-2" data-testid="discover-sort-pills">
          {SORTS.map(({ key, label }) => {
            const active = sort === key;
            return (
              <button
                key={key}
                data-testid={`discover-sort-${key}`}
                onClick={() => { setSort(key); setPage(1); }}
                className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${active ? "bg-emerald-800 text-white" : "bg-white text-emerald-900/70 ring-1 ring-emerald-900/10 hover:bg-mint"}`}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}

      {profiles === null ? (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">{[...Array(8)].map((_, i) => <Skeleton key={i} className="aspect-[3/4] rounded-3xl" />)}</div>
      ) : profiles.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-emerald-300 bg-white py-20 text-center" data-testid="discovery-empty">
          <Sparkles className="mb-3 h-10 w-10 text-amber-500" />
          <h3 className="font-serif text-2xl font-semibold text-emerald-900">You're all caught up</h3>
          <p className="mt-2 max-w-sm text-slate-500">No new recommendations right now. Try Browse or Search to explore more profiles.</p>
        </div>
      ) : (
        <>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="discovery-grid">
            {profiles.map((p, i) => (
              <ProfileCard key={p.id} profile={p} index={i} showActions={!isAssistant} onInterested={(id) => decide(id, "interested")} />
            ))}
          </div>
          {tab === "browse" && hasMore && (
            <div className="mt-8 flex justify-center">
              <button
                onClick={() => load(page + 1)}
                disabled={loadingMore}
                data-testid="discover-load-more"
                className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-emerald-800 shadow-sm ring-1 ring-emerald-900/10 transition-colors hover:bg-mint disabled:opacity-60"
              >
                {loadingMore ? <Loader2 className="h-4 w-4 animate-spin" /> : <Globe className="h-4 w-4" />} Load more
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
